import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { getEmptyPage, getOriginalPage, getPageSize } from 'editor-utils/template';
import useModalContext from 'components/context/modal';
import SortableList, { SortableItem } from 'react-easy-sort';
import produce from 'utils/produce';
import chunk from 'utils/chunk';
import uniq from 'utils/uniq';
import { capitalizeString } from 'utils/format';
import classNames from 'classnames';
import {
  Alignment,
  Button as BPButton,
  Intent,
  Navbar,
  Radio,
  RadioGroup,
} from '@blueprintjs/core';
import { Typography } from 'components/base';

import { Layout, Modal } from '../components/rich';
import { Button, Logo } from '../components/base';
import useEditorItemContext from '../context/editorItemContext';
import useEditorContext from '../context/editorContext';
import PageWrapper from '../new/components/pageWrapper';
import Page from '../components/page/preview';

const PageLabel = styled.span`
  width: 100%;
  padding: 10px 0;
  text-align: center;
  color: #26336b;
`;

const PageCell = styled.div`
  min-width: ${props => (props.$span ? 240 : 120)}px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: none;
`;

const PageSpread = styled.div`
  display: flex;
  transition: none;
  z-index: 1000;
  position: relative;

  * {
    user-select: none;
  }
  img {
    pointer-events: none;
  }
`;

const pageChunks = (pages = [], split = 1) => {
  if (pages.length === 0) {
    return [];
  }
  if (split === 2) {
    const [firstPage, ...otherPages] = pages;
    return [[firstPage], ...chunk(otherPages, 2)];
  }
  return chunk(pages, 1);
};

const withPageManager = Component => {
  const WrappedComponent = props => {
    const editorContext = useEditorContext();
    const modalContext = useModalContext();
    const { item, config, template } = useEditorItemContext();

    const [isOpen, setIsOpen] = useState(false);
    const [chunkValue, setChunkValue] = useState(config.withoutFirstBlankPage ? 1 : 2);

    const [selectedPageKeys, setSelectedPageKeys] = useState({});
    const [latestPageKeys, setLatestPageKeys] = useState([]);

    const openPageManager = () => {
      setSelectedPageKeys({});
      setIsOpen(true);
    };

    const closePageManager = () => {
      setSelectedPageKeys({});
      setIsOpen(false);
    };

    const handleOnDeletePages = () => {
      const pageKeys = Object.keys(selectedPageKeys);
      const label = pageKeys.length === 1 ? 'page' : 'pages';

      modalContext.confirm(
        <>
          Do you really want to delete selected {label}? <br />
          This action can&apos;t be taken back.
        </>,
        {
          title: `Delete Selected ${capitalizeString(label)}?`,
          onConfirm: () => {
            editorContext.removePageByKeys(item.id, pageKeys);
            setSelectedPageKeys({});
          },
        },
      );
    };

    const handleOnClonePages = () => {
      const pageKeys = Object.keys(selectedPageKeys);
      const label = pageKeys.length === 1 ? 'page' : 'pages';

      modalContext.confirm(<>Do you really want to clone selected {label}?</>, {
        title: `Clone Selected ${capitalizeString(label)}?`,
        onConfirm: () => {
          editorContext.duplicatePages(item.id, pageKeys);
          setSelectedPageKeys({});
        },
      });
    };

    const [emptyPage, originalPages] = useMemo(() => {
      if (!isOpen) {
        return [];
      }

      const [cover, ...pages] = item.pages;

      const type = 'pages';
      const [width, height] = getPageSize(template, type);

      return [
        getEmptyPage(template),
        pages.map(page => {
          const size = { width, height };
          if (page.span) {
            size.width *= 2;
          }
          // return { label, ...originalPage };
          return { ...page, label: page.pageLabel, size, type, visible: true };
        }),
      ];
    }, [item.pages, template, isOpen]);

    const selectAll = () => {
      setLatestPageKeys([]);
      setSelectedPageKeys(value => {
        if (Object.keys(value).length === 0) {
          return Object.fromEntries(originalPages.map(page => [page.key, true]));
        }
        return {};
      });
    };

    const selectPages = (pageKeys, shiftKey = false) => {
      let keys = pageKeys;

      if (shiftKey && latestPageKeys.length > 0) {
        const [from, to] = originalPages.reduce(
          (acc, page, index) => {
            if (pageKeys.indexOf(page.key) >= 0 || latestPageKeys.indexOf(page.key) >= 0) {
              return [Math.min(acc[0], index), Math.max(acc[1], index)];
            }
            return acc;
          },
          [Number.MAX_SAFE_INTEGER, Number.MIN_SAFE_INTEGER],
        );

        keys = originalPages
          .slice(from, to + 1)
          .map(({ key }) => key)
          .filter(key => latestPageKeys.indexOf(key) < 0);
      }

      setSelectedPageKeys(value =>
        produce(value, draft => {
          for (const pageKey of keys) {
            if (value[pageKey]) {
              setLatestPageKeys([]);
              delete draft[pageKey];
            } else {
              setLatestPageKeys(val => uniq([...val, pageKey]).slice(-chunkValue));
              draft[pageKey] = true;
            }
          }
        }),
      );
    };

    const handleOnPagesSortEnd = (oldIndex, newIndex) => {
      let fromIndex;
      let toIndex;
      let numberOfPagesToMove;

      if (chunkValue === 1) {
        fromIndex = oldIndex;
        toIndex = newIndex;
        numberOfPagesToMove = 1;
      } else if (chunkValue === 2) {
        if (newIndex === 0) {
          toIndex = 0;
        } else {
          toIndex = newIndex * 2 - 1;
        }
        if (oldIndex === 0) {
          numberOfPagesToMove = 1;
          fromIndex = 0;
          toIndex += 1;
        } else {
          numberOfPagesToMove = 2;
          fromIndex = oldIndex * 2 - 1;
        }
      }

      editorContext.movePage(item.id, true, fromIndex, toIndex, numberOfPagesToMove);
    };

    const handleOnHelpClick = () => {
      modalContext.confirm(
        <>
          <Typography.P className="mb-1">
            Pages can be managed as individual pages or as double-page spreads (facing pages). To
            rearrange pages, click and drag the page or spread to the desired location.
          </Typography.P>
          <Typography.P className="mb-1">
            Manage your book by selecting pages to clone or delete. Select a page or spread by
            clicking on it. You can select multiple at once. You can also select a range of pages by
            pressing the Shift key while clicking on the pages or spreads. (For example: clicking on
            pages 6 and 12 while holding Shift will select all pages between 6 and 12.)
          </Typography.P>
          <Typography.P className="mb-1">
            Click the &quot;Clone Selected Pages&quot; button to duplicate the page selection. The
            duplicate will be inserted in the book after the original page.
          </Typography.P>
          <Typography.P className="mb-1">
            Click the &quot;Delete Selected Pages&quot; button to delete the page selection. Confirm
            you want to delete the page(s) as this action cannot be undone
          </Typography.P>
          <Typography.P>Every change is automatically applied to your book.</Typography.P>
        </>,
        {
          title: 'Help',
          closeable: false,
          className: 'mw-660',
          confirmLabel: 'OK',
        },
      );
    };

    return (
      <>
        <Component
          {...props}
          openPageManager={openPageManager}
          closePageManager={closePageManager}
          isPageManagerOpen={isOpen}
        />
        {isOpen && (
          <Modal visible size="fullScreen" backgroundColor="primary">
            <Layout.Container style={{ justifyContent: 'space-between' }}>
              <div className="d-flex">
                <Button large p={0} onClick={closePageManager}>
                  <Logo white />
                </Button>
                <Modal.Paging>
                  <Modal.Heading>Manage Pages</Modal.Heading>
                </Modal.Paging>
                <Button
                  active
                  variant="tertiary"
                  large
                  onClick={handleOnHelpClick}
                  className="ml-1"
                >
                  Help
                </Button>
              </div>
              <div>
                <Button active variant="tertiary" large onClick={closePageManager}>
                  Back to Preview
                </Button>
              </div>
            </Layout.Container>

            <Navbar
              className="zIndexMax"
              style={{
                margin: '1rem 0 0 0',
                padding: '0 1.5rem',
              }}
            >
              <Navbar.Group align={Alignment.LEFT}>
                {!config.withoutFirstBlankPage && (
                  <RadioGroup
                    selectedValue={chunkValue}
                    inline
                    onChange={e => {
                      const value = parseInt(e.target.value, 10);
                      setChunkValue(value);
                      if (value === 2) {
                        setSelectedPageKeys({});
                      }
                      setLatestPageKeys([]);
                    }}
                  >
                    <Radio value={1} className="mb-0">
                      Arrange & Manage Individual Pages
                    </Radio>
                    <Radio value={2} className="mb-0">
                      Arrange & Manage Double Page Spreads
                    </Radio>
                  </RadioGroup>
                )}
              </Navbar.Group>
              <Navbar.Group align={Alignment.RIGHT}>
                <BPButton onClick={selectAll} style={{ marginRight: 10 }}>
                  {Object.keys(selectedPageKeys).length === 0
                    ? `Select All (${originalPages.length})`
                    : `Unselect All (${Object.keys(selectedPageKeys).length})`}
                </BPButton>
                <BPButton
                  onClick={handleOnClonePages}
                  intent={Intent.WARNING}
                  disabled={Object.keys(selectedPageKeys).length === 0}
                  style={{ marginRight: 10 }}
                >
                  Clone Selected Pages
                </BPButton>
                <BPButton
                  onClick={handleOnDeletePages}
                  intent={Intent.DANGER}
                  disabled={Object.keys(selectedPageKeys).length === 0}
                >
                  Delete Selected Pages
                </BPButton>
              </Navbar.Group>
            </Navbar>
            <Layout.Main
              style={{
                margin: 0,
                padding: 0,
                background: 'white',
                alignItems: 'start',
                overflowY: 'unset',
              }}
            >
              <div
                className="d-flex w-100"
                style={{
                  flexWrap: 'wrap',
                  justifyContent: 'start',
                  flexDirection: 'column',
                }}
              >
                <div
                  className="d-flex mb-3"
                  style={{
                    flexWrap: 'wrap',
                    justifyContent: 'start',
                    alignItems: 'center',
                    padding: '1.5rem 0 1.5rem 1.5rem',
                    overflowY: 'scroll',
                  }}
                >
                  <SortableList
                    onSortEnd={handleOnPagesSortEnd}
                    className="w-100 flex-row flex-wrap d-flex"
                    allowDrag
                  >
                    {chunkValue === 2 && (
                      <PageCell>
                        <PageWrapper noMenu>
                          <Page page={emptyPage} fullWidth />
                        </PageWrapper>
                      </PageCell>
                    )}
                    {pageChunks(originalPages, chunkValue).map((originalPageArray, index) => {
                      const keys = originalPageArray.map(({ key }) => key);
                      return (
                        <SortableItem key={keys.join('-')}>
                          <PageSpread>
                            {originalPageArray.map((originalPage, idx) => (
                              <PageCell
                                key={originalPage.key}
                                id={`thumb-${index + 1}`}
                                $span={originalPage.span}
                                className={classNames({
                                  'mr-3':
                                    chunkValue === 1 ||
                                    (chunkValue === 2 && idx === originalPageArray.length - 1),
                                })}
                              >
                                <PageWrapper noMenu>
                                  <Page
                                    page={originalPage}
                                    photos={item.photos}
                                    template={template}
                                    fullWidth
                                    serverSideDPI={16}
                                    onClick={e => selectPages(keys, e.shiftKey)}
                                    className={
                                      selectedPageKeys[originalPage.key] && 'editor-page-active'
                                    }
                                  />
                                </PageWrapper>
                                <PageLabel>{originalPage.label}</PageLabel>
                              </PageCell>
                            ))}
                          </PageSpread>
                        </SortableItem>
                      );
                    })}
                  </SortableList>
                </div>
              </div>
            </Layout.Main>
          </Modal>
        )}
      </>
    );
  };

  return WrappedComponent;
};

export default withPageManager;
